import React, {useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import {useTheme} from "@emotion/react";
import CustomLabel from "./CustomLabel";
import {useModelContext} from "../../../context/ModelContextProvider";
import ThreeDotsLoading from "./ThreeDotsLoading";
import {AiOutlineCheck} from "react-icons/ai";
import {lang} from "../../../lang";
import ErrorMsg from "./ErrorMsg";
import {
    BROADCAST_ON_NEW_PW_CHANGED,
    getImageUrl,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";

/*
    props
    name : string
    placeholder : string
    need_label : bool = 라벨이 필요 여부
    label_name : string = 라벨명
    read_only : bool = 수정 불가 여부
    current_value : obj {value, error_msg} = 부모 컴포넌트가 가진 info(ref)
    on_change : func(return void) = 부모 컴포넌트의 info(ref)를 변경
    is_error : bool = 부모 컴포넌트에서 validate체크 후 error발생 여부
    regex : string = 정규식
    _ref: ref
 */
const CustomPasswordInput = (props) => {
    // props 초기화
    const {
        name,
        placeholder = null,
        need_label = true,
        label_name = null,
        read_only = false,
        current_value = null,
        on_change = null,
        is_error = false,
        regex = null,
        _ref,
    } = props;

    const {broadcast} = useApplicationContext();
    const {account_model, isSuccess} = useModelContext();

    // state
    const [is_filled, setIsFilled] = useState(false);
    const [is_not_validate, setIsNotValidate] = useState(is_error);
    const [is_checked, setIsChecked] = useState(false);
    const [is_password_show, setIsPasswordShow] = useState(false);
    const [is_loading, setIsLoading] = useState(false);
    const [error_msg, setErrorMsg] = useState(current_value.error_msg);

    // ref
    const new_pw_value = useRef(); // 새로운 비밀번호 (new_pw에서 onChange시에 변경)

    // css style
    const {view} = useComponentStyle({
        read_only,
        is_filled,
        is_not_validate,
        is_password_show,
    });

    useEffect(() => {
        // is_not_validate 등의 state가 변경되어도 그대로 forcus 유지를 위해
        if (is_checked) {
            _ref.current.blur();
        } else {
            _ref.current.focus();
        }

        if (current_value.value) {
            setIsFilled(true);
        }
    }, [is_password_show, is_filled, is_not_validate, is_checked, error_msg, _ref]);

    useEffect(() => {
        const unlisten = broadcast.listen([BROADCAST_ON_NEW_PW_CHANGED], ({value}) => {
            new_pw_value.current = value;

            if (name === "check_new_pw" && is_checked) {
                // new_pw의 값을 지울 때(Backspace) check_new_pw의 에러등을 변경("일치하지 않습니다")
                validateNewAndCheckPassword(current_value.value);
            }
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    }, [broadcast, is_checked, name]);

    // 비밀번호 보여주기 / 숨기기
    const onToggleShowIcon = () => {
        setIsPasswordShow(!is_password_show);
    };

    // new_pw, check_new_pw 의 validate체크
    const validateNewAndCheckPassword = (pw) => {
        if (name === "new_pw") {
            if (!regex.test(pw)) {
                setIsNotValidate(true);
                setIsChecked(false);
                setIsFilled(false);

                onHandleValidateError();
            } else {
                setIsNotValidate(false);
                setIsChecked(true);
                setIsFilled(true);

                current_value.error_msg = null;
                setErrorMsg(null);
            }
        } else if (name === "check_new_pw") {
            if (pw !== new_pw_value.current) {
                setIsNotValidate(true);
                setIsChecked(false);
                setIsFilled(false);

                onHandleValidateError();
            } else {
                setIsNotValidate(false);
                setIsChecked(true);
                setIsFilled(true);

                current_value.error_msg = null;
                setErrorMsg(null);
            }
        }
    };

    // prev_pw의 validate 체크
    const validatePrevPassword = async (pw) => {
        setIsLoading(true);

        const check_prev_password_res = await account_model.postCheckPrevPassword({password: JSON.stringify(pw)});
        if (isSuccess(check_prev_password_res)) {
            setIsNotValidate(false);
            setIsChecked(true);
            setIsFilled(true);

            current_value.error_msg = null;
        } else {
            setIsNotValidate(true);
            setIsChecked(false);
            setIsFilled(false);

            onHandleValidateError();
        }
        setIsLoading(false);
    };

    // 값이 비었을 경우 (필수항목입니다)
    const onHandleEmptyError = () => {
        setIsChecked(false);
        setIsFilled(false);
        setIsNotValidate(true);

        current_value.error_msg = lang.modify_password.empty_modify_password;
        setErrorMsg(lang.modify_password.empty_modify_password);
    };

    const onHandleValidateError = () => {
        if (name === "prev_pw") {
            current_value.error_msg = lang.modify_password.not_matched_current_password;
            setErrorMsg(lang.modify_password.not_matched_current_password);
        } else if (name === "new_pw") {
            current_value.error_msg = lang.login.check_login_password;
            setErrorMsg(lang.login.check_login_password);
        } else if (name === "check_new_pw") {
            current_value.error_msg = lang.modify_password.not_matched_current_password;
            setErrorMsg(lang.modify_password.not_matched_current_password);
        }
    };

    return (
        <view.InputWrapper>
            {need_label && <CustomLabel htmlFor={`id_${name}`} label_name={label_name} ref={_ref} />}
            <view.Input
                autoComplete={"off"}
                ref={_ref}
                id={`id_${name}`}
                type={is_password_show ? "text" : "password"}
                placeholder={placeholder}
                readOnly={read_only}
                defaultValue={current_value.value}
                onKeyUp={(e) => {
                    if (e.key === "Backspace") {
                        if (e.currentTarget.value < 1) {
                            onHandleEmptyError();
                        } else {
                            validateNewAndCheckPassword(e.currentTarget.value);
                        }
                    }
                }}
                onChange={(e) => {
                    on_change(e.currentTarget.value);

                    if (e.currentTarget.value.length < 1) {
                        onHandleEmptyError();
                    }

                    if (name === "new_pw") {
                        broadcast.send(BROADCAST_ON_NEW_PW_CHANGED, e.currentTarget.value);
                    }

                    if (name === "check_new_pw") {
                        validateNewAndCheckPassword(e.currentTarget.value);
                    }

                    _ref.current.focus();
                }}
                onBlur={async (e) => {
                    if (e.currentTarget.value.length >= 1) {
                        if (name === "prev_pw") {
                            await validatePrevPassword(e.currentTarget.value);
                        } else if (name === "new_pw") {
                            validateNewAndCheckPassword(e.currentTarget.value);
                        }

                        if (name === "new_pw") {
                            broadcast.send(BROADCAST_ON_NEW_PW_CHANGED, e.currentTarget.value);
                        }
                    } else {
                        setIsFilled(false);
                    }
                }}
            />
            {props.name !== "prev_pw" && (
                <view.ShowPasswordIcon
                    onMouseDown={(e) => {
                        // 아이콘 클릭시 blur이벤트 막기
                        e.preventDefault();
                    }}
                    onClick={onToggleShowIcon}
                />
            )}
            {is_loading && <ThreeDotsLoading />}
            {is_checked && !is_loading && (
                <span className={"success"}>
                    {name === "new_pw" ? (
                        <AiOutlineCheck />
                    ) : (
                        <>
                            <AiOutlineCheck /> 일치
                        </>
                    )}
                </span>
            )}
            {!is_checked && !is_loading && is_not_validate && <ErrorMsg error_msg={error_msg} />}
        </view.InputWrapper>
    );
};

export default CustomPasswordInput;

export const useComponentStyle = ({read_only, is_filled, is_not_validate, is_password_show}) => {
    const theme = useTheme();
    return {
        view: {
            InputWrapper: styled.div({
                position: "relative",
                marginBottom: "1.5em",
                span: {
                    position: "absolute",
                    width: "100%",
                    left: "0",
                    bottom: "-1.5em",
                },
                ".success": {
                    fontSize: theme.font_size.f14,
                    color: theme.colors.bearu_primary,
                },
            }),
            Input: styled.input({
                marginTop: "1em",
                boxSizing: "border-box",
                width: "100%",
                height: "2em",
                outline: "none",
                border: "none",
                fontSize: theme.font_size.f16,
                borderBottom: read_only
                    ? "none"
                    : is_filled
                    ? `1px solid ${theme.colors.black}`
                    : is_not_validate
                    ? "1px solid red"
                    : `1px solid ${theme.colors.lg3}`,
                "&:focus": read_only
                    ? {borderBottom: "none"}
                    : is_not_validate
                    ? {borderBottom: "1px solid red"}
                    : {borderBottom: `1px solid ${theme.colors.bearu_primary}`},
            }),
            ShowPasswordIcon: styled.div({
                width: "1.8em",
                height: "1.8em",
                cursor: "pointer",
                position: "absolute",
                bottom: "5%",
                right: "2%",
                background: `url(${
                    is_password_show
                        ? getImageUrl("image/common/ic_eye_open.png")
                        : getImageUrl("image/common/ic_eye_off.png")
                }) center center/contain no-repeat`,
            }),
        },
    };
};
